import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  postMarker,
  deleteMarker,
  likeMarker,
  undoLikeMarker,
  updateMarker
} from "./thunk";
import { hydrate } from "@/lib";
import type { Marker } from "@/types";

export interface MarkerInitialState {
  marker: Partial<Marker>;
  status: "idle" | "fetching" | "error" | "saving";
  error: string;
  webPinDetails: any;
  isMarkerInsidePolygon: boolean;
  isEditMode: boolean;
  prevMarkers: any;
  currentPageNumber: number;
}
const initialState: MarkerInitialState = {
  marker: {},
  isMarkerInsidePolygon: true,
  webPinDetails: [],
  isEditMode: false,
  status: "idle",
  error: "",
  prevMarkers: [],
  currentPageNumber: 1
};

export const placesMarkerSlice = createSlice({
  name: "placesMarkerSlice",
  initialState,
  reducers: {
    setMarker: (state, action) => {
      state.marker = action.payload;
    },
    toggleEditMode: (state, action) => {
      state.isEditMode = action.payload;
    },
    setIsMarkerInsidePolygon: (state, action) => {
      state.isMarkerInsidePolygon = action.payload;
    },
    resetIsMarkerInsidePolygon: (state) => {
      state.isMarkerInsidePolygon = initialState.isMarkerInsidePolygon;
    },
    setLatLng: (state, action) => {
      const { lng, lat, address } = action.payload;
      const marker = state.marker as Marker;
      marker.lng = lng;
      marker.lat = lat;
      marker.address = address;
    },
    resetMarker: (state) => {
      state.marker = initialState.marker;
      state.isEditMode = initialState.isEditMode;
    },
    setMarkerError: (state, action) => {
      state.error = action.payload;
    },
    resetMarkerError: (state) => {
      state.error = initialState.error;
    },
    setMarkerDetailsWithCoord: (state, action) => {
      const { lng, lat, address, isMarkerInsidePolygon } = action.payload;
      const marker = state.marker as Marker;
      marker.lng = lng;
      marker.lat = lat;
      marker.address = address;
      state.isMarkerInsidePolygon = isMarkerInsidePolygon;
    },
    setPreviousMarker: (state, action) => {
      state.prevMarkers = action.payload;
    },
    setCurrentPageNumber: (state, action) => {
      state.currentPageNumber = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrate, (state, action: PayloadAction<string>) => ({
        ...state,
        ...action.payload[placesMarkerSlice.name]
      }))
      .addCase(postMarker.pending, (state) => {
        state.status = "fetching";
      })
      .addCase(postMarker.fulfilled, (state) => {
        state.status = "idle";
        state.isEditMode = false;
      })
      .addCase(postMarker.rejected, (state, action) => {
        const { message } = action.error;

        state.status = "error";
        state.error = message;
      })
      .addCase(deleteMarker.pending, (state) => {
        state.status = "fetching";
      })
      .addCase(deleteMarker.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteMarker.rejected, (state, action) => {
        const { message } = action.error;
        state.status = "error";
        state.error = message;
      })
      .addCase(likeMarker.pending, (state) => {
        state.status = "saving";
      })
      .addCase(likeMarker.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(likeMarker.rejected, (state, action) => {
        const { message } = action.error;
        state.status = "error";
        state.error = message;
      })
      .addCase(undoLikeMarker.pending, (state) => {
        state.status = "saving";
      })
      .addCase(undoLikeMarker.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(undoLikeMarker.rejected, (state, action) => {
        const { message } = action.error;
        state.status = "error";
        state.error = message;
      })
      .addCase(updateMarker.pending, (state) => {
        state.status = "fetching";
      })
      .addCase(updateMarker.fulfilled, (state) => {
        state.isEditMode = false;
        state.status = "idle";
      })
      .addCase(updateMarker.rejected, (state, action) => {
        const { message } = action.error;

        state.status = "error";
        state.error = message;
      });
  }
});

export const {
  setCurrentPageNumber,
  setMarker,
  toggleEditMode,
  setLatLng,
  resetMarker,
  setMarkerError,
  resetMarkerError,
  setIsMarkerInsidePolygon,
  resetIsMarkerInsidePolygon,
  setMarkerDetailsWithCoord,
  setPreviousMarker
} = placesMarkerSlice.actions;
